import { msg } from "@lingui/core/macro";
import { createEffect, ErrorBoundary, type JSX, Show } from "solid-js";
import { AlertCircle, RefreshCw, RotateCcw } from "@fleet/icons";

import { Button } from "@/components/ui/button";
import { GlassSurface } from "@fleet/ui";
import { ErrorIllustration } from "@/components/ui/illustrations";
import { useI18n } from "@/providers/i18n-provider";

interface AppErrorBoundaryProps {
  children: JSX.Element;
  /**
   * "page" → premium full-screen fallback (large illustration, two actions).
   * "region" → compact card for a section (session, stats) without covering
   * the rest of the app. Default: "page".
   */
  variant?: "page" | "region";
  /** Region label for logging (e.g. "session", "stats"). */
  label?: string;
}

function logError(label: string | undefined, error: unknown) {
  // Centralizes logging. Never a blank screen: fallback always renders.
  console.error(`[ErrorBoundary${label ? `:${label}` : ""}]`, error);
}

function errorMessage(error: unknown): string | undefined {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return undefined;
}

/** Full-screen fallback, theme-aware and accessible. */
function PageFallback(props: { error: unknown; reset: () => void }) {
  const { t } = useI18n();
  return (
    <div class="flex flex-1 items-center justify-center p-4 min-h-[60dvh]" role="alert">
      <GlassSurface class="max-w-md w-full p-8 flex flex-col items-center text-center space-y-6 animate-scale-in">
        <ErrorIllustration class="h-32 w-32 animate-float" />
        <div class="space-y-2">
          <h2 class="text-2xl font-bold tracking-tight">{t(msg`Something went wrong`)}</h2>
          <p class="text-muted-foreground text-sm">
            {t(msg`This screen ran into an unexpected problem. Your data is safe — try again.`)}
          </p>
          <Show when={errorMessage(props.error)}>
            {(detail) => (
              <p class="text-xs text-muted-foreground/70 font-mono break-words pt-1">{detail()}</p>
            )}
          </Show>
        </div>
        <div class="flex gap-3 w-full">
          <Button
            variant="outline"
            class="flex-1 gap-2"
            onClick={() => {
              globalThis.location.reload();
            }}
          >
            <RotateCcw class="h-4 w-4" />
            {t(msg`Reload`)}
          </Button>
          <Button
            class="flex-1 gap-2"
            onClick={() => {
              props.reset();
            }}
          >
            <RefreshCw class="h-4 w-4" />
            {t(msg`Retry`)}
          </Button>
        </div>
      </GlassSurface>
    </div>
  );
}

/** Compact fallback for a specific region (does not cover rest of app). */
function RegionFallback(props: { error: unknown; reset: () => void }) {
  const { t } = useI18n();
  return (
    <div
      role="alert"
      class="rounded-2xl border border-destructive/20 bg-destructive/5 p-5 flex flex-col items-center text-center gap-3 animate-scale-in"
    >
      <div class="h-11 w-11 rounded-full bg-destructive/10 flex items-center justify-center">
        <AlertCircle class="h-6 w-6 text-destructive" />
      </div>
      <div class="space-y-1">
        <p class="font-semibold">{t(msg`This section couldn't load`)}</p>
        <p class="text-sm text-muted-foreground">
          {t(msg`Something went wrong here. The rest of the app keeps working.`)}
        </p>
      </div>
      <Button
        size="sm"
        variant="outline"
        class="gap-2"
        onClick={() => {
          props.reset();
        }}
      >
        <RefreshCw class="h-3.5 w-3.5" />
        {t(msg`Retry`)}
      </Button>
    </div>
  );
}

/** Unified fallback: logs and selects variant. Top-level component. */
function ErrorFallback(props: {
  error: unknown;
  reset: () => void;
  variant?: "page" | "region";
  label?: string;
}) {
  createEffect(() => {
    logError(props.label, props.error);
  });
  return (
    <Show
      when={props.variant === "region"}
      fallback={<PageFallback error={props.error} reset={props.reset} />}
    >
      <RegionFallback error={props.error} reset={props.reset} />
    </Show>
  );
}

/**
 * Solid ErrorBoundary with PREMIUM theme-aware fallback. Catches render
 * errors in its subtree (which router `errorComponent` DOES NOT cover),
 * logs and offers "Retry" (resets boundary) and "Reload".
 */
export function AppErrorBoundary(props: AppErrorBoundaryProps) {
  return (
    <ErrorBoundary
      // oxlint-disable-next-line react/no-unstable-nested-components -- Solid's ErrorBoundary fallback is a render prop, not a component definition
      fallback={(err: unknown, reset: () => void) => (
        <ErrorFallback error={err} reset={reset} variant={props.variant} label={props.label} />
      )}
    >
      {props.children}
    </ErrorBoundary>
  );
}
